import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  MenuItem,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import apiClient from "../utils/apiClient";
import VendorPayoutStatus from "./VendorPayoutStatus";

const VendorPayoutForm = () => {
  const [form, setForm] = useState({
    accountHolderName: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    branchName: "",
    upiId: "",
    payoutMethod: "bank_transfer",
  });

  const [errors, setErrors] = useState({});

  const [loading, setLoading] = useState(false); // submit
  const [fetchLoading, setFetchLoading] = useState(false); // fetch

  const fetchDetails = async () => {
    try {
      setFetchLoading(true);

      const res = await apiClient.get("/api/vendor/payout-details");

      if (res?.data?.data?.payoutId) {
        setForm({ ...res.data.data });
      }

    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails()
  }, [])

  // ✅ Handle Change
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ✅ Validation
  const validate = () => {
    let temp = {};

    if (!form.accountHolderName) temp.accountHolderName = "Account holder name required";
    if (form.payoutMethod === "bank_transfer") {
      if (!form.bankName) temp.bankName = "Bank name required";
      if (!form.accountNumber) temp.accountNumber = "Account number required";
      if (!form.ifscCode) temp.ifscCode = "IFSC code required";
      if (!form.branchName) temp.branchName = "Branch name required";
    }
    if (form.payoutMethod === "upi" && !form.upiId) {
      temp.upiId = "UPI ID required";
    }

    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  // ✅ Submit / PUT API
  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);

    apiClient
      .post("/api/vendor/payout-details", form)
      .then(() => {
        toast.success("Payout details submitted successfully!")
        fetchDetails()
      })
      .catch((err) =>
        toast.error(err.response?.data?.message || "Failed to save data")
      ).finally(() => {
        setLoading(false);
      });

  };

  if (fetchLoading) {
    return (
      <Box textAlign="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 4, mx: "auto" }}>
      {form?.payoutId && 
      <VendorPayoutStatus data={form}/>
}
      <Typography variant="h5" mb={3}>
        Vendor Payout Details
      </Typography>

      <Grid container spacing={2} mt={1}>
        {/* Account Holder */}
        <Grid item size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Account Holder Name"
            name="accountHolderName"
            value={form.accountHolderName}
            onChange={handleChange}
            error={!!errors.accountHolderName}
            helperText={errors.accountHolderName}
          />
        </Grid>

        {/* Payout Method */}
        <Grid item size={{ xs: 12 }}>
          <TextField
            select
            fullWidth
            label="Payout Method"
            name="payoutMethod"
            value={form.payoutMethod}
            onChange={handleChange}
            error={!!errors.payoutMethod}
            helperText={errors.payoutMethod}
          >
            <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
            <MenuItem value="upi">UPI</MenuItem>
          </TextField>
        </Grid>

        {/* Bank Fields */}
        {form.payoutMethod === "bank_transfer" && (
          <>
            <Grid item size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Bank Name"
                name="bankName"
                value={form.bankName}
                onChange={handleChange}
                error={!!errors.bankName}
                helperText={errors.bankName}
              />
            </Grid>

            <Grid item size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Account Number"
                name="accountNumber"
                value={form.accountNumber}
                onChange={handleChange}
                error={!!errors.accountNumber}
                helperText={errors.accountNumber}
              />
            </Grid>

            <Grid item size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="IFSC Code"
                name="ifscCode"
                value={form.ifscCode}
                onChange={handleChange}
                error={!!errors.ifscCode}
                helperText={errors.ifscCode}
              />
            </Grid>

            <Grid item size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Branch Name"
                name="branchName"
                value={form.branchName}
                onChange={handleChange}
                error={!!errors.branchName}
                helperText={errors.branchName}
              />
            </Grid>
          </>
        )}

        {/* UPI */}
        {form.payoutMethod === "upi" && (
          <Grid item size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="UPI ID"
              name="upiId"
              value={form.upiId}
              onChange={handleChange}
              error={!!errors.upiId}
              helperText={errors.upiId}
            />
          </Grid>
        )}

        {/* Submit */}
        <Grid item size={{ xs: 12 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit for Approval"}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default VendorPayoutForm;